import { GameLoop } from "~/engine/game-loop";
import { runSandSimulation } from "~/engine/sand";
import { PlayerEntity } from "~/entities/player";
import { ThreeRenderer } from "~/renderers/three-renderer";
import { InputManager } from "~/services/input-manager";
import { useGameStore } from "~/state/game-store";
import { GrenadeSystem } from "~/systems/grenade-system";
import { MiningSystem } from "~/systems/mining-system";
import { MovementSystem } from "~/systems/movement-system";
import { WormSystem } from "~/systems/worm-system";
import { VoxelTerrain } from "~/terrain/voxel-terrain";

// Game engine configuration
export type GameEngineConfig = {
  containerElement: HTMLElement;
};

/**
 * Singleton class that manages the entire game engine outside of React
 */
export class GameEngineManager {
  // Engine components
  public gameLoop: GameLoop | null = null;
  public voxelTerrain: VoxelTerrain | null = null;
  public renderer: ThreeRenderer | null = null;
  public miningSystem: MiningSystem | null = null;
  public movementSystem: MovementSystem | null = null;
  public inputManager: InputManager | null = null;

  public player: PlayerEntity | null = null;
  public wormSystem: WormSystem | null = null;
  public grenadeSystem: GrenadeSystem | null = null;

  // Game state store
  // TODO: kill this??
  public gameStore: typeof useGameStore | null = null;

  // Configuration
  private containerElement: HTMLElement | null = null;

  // Initialization state
  private isInitialized = false;

  /**
   * Initialize the game engine
   */
  public async initialize(config: GameEngineConfig): Promise<void> {
    if (this.isInitialized) {
      console.warn("Game engine already initialized");
      return;
    }

    this.containerElement = config.containerElement;

    this.gameStore = useGameStore;

    const renderMode = this.gameStore.getState().settings.renderMode;
    this.renderer = new ThreeRenderer(this.containerElement, {
      backgroundColor: "#87CEEB",
      antialias: true,
      renderMode,
    });

    // Initialize input manager and mining/movement systems

    // First create mining system
    this.miningSystem = new MiningSystem();

    // Then create movement system

    this.inputManager = new InputManager();

    // Listen for escape key to cycle through menu states
    this.inputManager.on("escapeKeyDown", (evt) => {
      const state = this.gameStore?.getState();
      if (!state) return;

      // if (state.openMenu === "settings") {
      //   // If settings is open, go back to pause menu
      //   state.setOpenMenu("pause");
      // } else if (state.openMenu) {
      //   // If pause menu is open, close it
      //   state.setOpenMenu(null);
      // } else {
      if (state.openMenu == null) {
        evt.stopPropagation();
        evt.preventDefault();

        // If nothing is open, open pause menu
        state.setOpenMenu("pause");
      }
    });

    this.inputManager.on("loadoutKeyDown", (loadoutNumber) => {
      const state = this.gameStore?.getState();
      if (!state) return;
      state.setLoadout(loadoutNumber);
    });

    // open inventory on "E"
    this.inputManager.on("inventoryKeyDown", () => {
      const state = this.gameStore?.getState();
      if (!state) return;
      state.setOpenMenu(state.openMenu === "inventory" ? null : "inventory");
    });

    // Initialize game loop
    this.gameLoop = new GameLoop();

    // let prevPlayerX: number | null = null,
    //   prevPlayerY: number | null = null;

    let runTick = 0;

    // Register fixed update callback
    this.gameLoop.onFixedUpdate((deltaTime) => {
      if (!this.renderer) return;

      this.player?.update(deltaTime);

      // TEMP: camera zoom controls
      if (this.inputManager?.keyStates["-"]) {
        this.renderer.zoomCameraBy(8.0);
      } else if (this.inputManager?.keyStates["="]) {
        this.renderer.zoomCameraBy(-8.0);
      }

      // Update systems
      this.miningSystem?.update(deltaTime);

      this.movementSystem?.update(deltaTime);

      this.wormSystem?.update(deltaTime);

      this.grenadeSystem?.update(deltaTime);

      // run sand simulation
      if (this.voxelTerrain) {
        runSandSimulation(runTick);
      }

      const playerPosition = this.player?.position;
      if (playerPosition) {
        // Load and unload chunks, and update changed voxels within each chunk
        void this.renderer.updateVisibleTerrain(playerPosition);
      }

      runTick++;
    });

    const loadedStartPos = this.gameStore.getState().playerPosition;

    let { playerStartPos } = await this.initializeMap();

    if (loadedStartPos.x !== 0 && loadedStartPos.y !== 0) {
      playerStartPos = loadedStartPos;
    }

    this.player = new PlayerEntity();
    this.player.setPosition(playerStartPos.x, playerStartPos.y);

    this.movementSystem = new MovementSystem(this.player);

    // AI systems
    this.wormSystem = new WormSystem();

    // Grenades system
    this.grenadeSystem = new GrenadeSystem();

    // Fire grenade on primary click when in grenade mode
    this.inputManager.on("pointerPrimaryDown", () => {
      if (!this.player || !this.grenadeSystem || !this.gameStore) return;
      const state = this.gameStore.getState();
      if (state.playerUpgrades.miningMode !== "grenade") return;

      const origin = this.player.pointerOrigin;
      const angle = this.player.pointerAngle;
      this.grenadeSystem.spawnGrenade(origin, angle);
    });

    // Start the game loop
    this.gameLoop.start();

    // Start render loop
    this.renderer.startRenderLoop();

    // Subscribe to render mode changes
    const unsubscribe = this.gameStore.subscribe((state, prevState) => {
      if (state.settings.renderMode !== prevState.settings.renderMode) {
        this.renderer?.updateRenderMode(state.settings.renderMode);
      }
    });
    this.unsubscribers.push(unsubscribe);

    this.isInitialized = true;
  }
  private unsubscribers: (() => void)[] = [];

  private async initializeMap() {
    if (!this.renderer || !this.gameStore)
      throw new Error("Renderer or gameStore not initialized");

    const { baseSeed } = this.gameStore.getState();

    // Initialize voxel terrain manager
    this.voxelTerrain = new VoxelTerrain({
      // TODO: could simplify stuff by making this 1, but maybe it's better to keep the concept for potential future performance optimizations
      voxelSize: 8,
      chunkWidthInVoxels: 64,
      chunkHeightInVoxels: 64,
      // worldWidthInChunks: 20,
      worldHeightInChunks: 100,
      seed: baseSeed,
      mineralDensity: 0.5,
    });

    // const startX = Math.floor(this.voxelTerrain.worldWidthInVoxels / 2);
    const startX = 0;

    // compute the initial player position
    const {
      voxelPos: { y: maxVoxelY },
    } = await this.voxelTerrain.getMaxVoxelY(startX);
    const spawnAboveGround = 10; // spawn 10 voxels above the ground
    const playerStartPos = this.voxelTerrain.voxelToWorld({
      x: startX,
      y: maxVoxelY + spawnAboveGround,
    });

    // initial load of terrain.
    await this.renderer.updateVisibleTerrain(playerStartPos);
    console.log("Loaded initial chunks:", this.renderer.loadedChunkCount());

    return { playerStartPos };
  }

  /**
   * Cleanup and shutdown the game engine
   */
  public shutdown(): void {
    if (!this.isInitialized) return;

    console.log("shutting down game engine");

    for (const unsubscribe of this.unsubscribers) unsubscribe();
    this.unsubscribers.length = 0;

    this.gameLoop?.dispose();
    this.inputManager?.dispose();
    this.voxelTerrain?.dispose();
    this.renderer?.dispose();
    this.miningSystem?.dispose();
    this.movementSystem?.dispose();
    this.wormSystem?.dispose();
    this.grenadeSystem?.dispose();

    // Reset state
    this.gameLoop = null;
    this.inputManager = null;
    this.voxelTerrain = null;
    this.renderer = null;
    this.miningSystem = null;
    this.movementSystem = null;
    this.wormSystem = null;
    this.grenadeSystem = null;
    this.gameStore = null;

    this.isInitialized = false;
  }
}
